import React, { useState } from "react";
import { Mail, Send, Instagram, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { api, formatApiError } from "@/lib/api";
import { SITE_META } from "@/data/staticContent";

export default function Contacto() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!name || !email || !message) return;
        setLoading(true);
        try {
            await api.post("/contact", { name, email, message });
            toast.success(
                "Mensaje enviado. Te leerá @NitanAdicto cuando termine de no responder a otros.",
            );
            setName("");
            setEmail("");
            setMessage("");
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "No pudimos enviar el mensaje. Inténtalo de nuevo.",
            );
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <SEO
                title="Contacto · Pero no me ofrezcas droga"
                description="¿Tienes algo que decir, una cicatriz que compartir o una recaída que comentar? Envíame un mensaje. Sin soporte técnico. Sin horario de atención. Pero no me ofrezcas droga."
                path="/contacto"
                keywords="contacto NiTanAdicto, Nelson González, escribir, recuperación, no me ofrezcas droga"
            />

            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <p className="eyebrow mb-6">Departamento de mensajes · 12</p>
                    <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mb-4">
                        Contacto<span className="text-[var(--danger)]">.</span>
                    </h1>
                    <p className="font-hand text-3xl md:text-5xl text-[var(--warning)] -rotate-2 inline-block mb-8">
                        Pero no me ofrezcas droga.
                    </p>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed">
                        Sin soporte técnico. Sin horario de atención. Pero sí
                        leo todos los mensajes. A veces.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24" data-testid="contacto-section">
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                        {/* Form */}
                        <form
                            onSubmit={submit}
                            className="lg:col-span-7 space-y-8"
                            data-testid="contacto-form"
                        >
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                    Nombre · campo obligatorio
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="¿Cómo te llamas hoy?"
                                    className="input-dossier"
                                    data-testid="contacto-name"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                    Email · campo obligatorio
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu.email@dominio.cl"
                                    className="input-dossier"
                                    data-testid="contacto-email"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                    Mensaje · sin filtros
                                </label>
                                <textarea
                                    required
                                    rows={7}
                                    value={message}
                                    onChange={(e) =>
                                        setMessage(e.target.value)
                                    }
                                    placeholder="Escribe lo que necesites. Tranquilo, ya empezó la terapia."
                                    className="input-dossier resize-none"
                                    data-testid="contacto-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-brutal btn-brutal-danger disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="contacto-submit"
                            >
                                <Send className="w-4 h-4" />
                                {loading ? "Enviando…" : "Enviar mensaje"}
                            </button>
                        </form>

                        {/* Info side */}
                        <aside className="lg:col-span-5 space-y-8">
                            <div className="dossier">
                                <div className="dossier-header">
                                    <span>Información de contacto</span>
                                    <span className="text-[var(--danger)]">
                                        Activo
                                    </span>
                                </div>
                                <div className="dossier-content space-y-4">
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-1">
                                            Email
                                        </p>
                                        <a
                                            href={`mailto:${SITE_META.contactEmail}`}
                                            className="font-mono text-sm text-[var(--paper-2)] hover:text-[var(--danger)] break-all"
                                            data-testid="contacto-email-link"
                                        >
                                            <Mail className="inline w-3.5 h-3.5 mr-2" />
                                            {SITE_META.contactEmail}
                                        </a>
                                    </div>
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-1">
                                            Redes
                                        </p>
                                        <div className="space-y-2">
                                            <a
                                                href={SITE_META.socials.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-sm text-[var(--paper-2)] hover:text-[var(--danger)] flex items-center gap-2"
                                            >
                                                <Instagram className="w-3.5 h-3.5" />
                                                @nitanadicto
                                            </a>
                                            <a
                                                href={SITE_META.socials.tiktok}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-sm text-[var(--paper-2)] hover:text-[var(--danger)] flex items-center gap-2"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                                TikTok
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-2 border-dashed border-[var(--warning)] p-6">
                                <p className="font-hand text-2xl text-[var(--warning)] -rotate-2 mb-3">
                                    Si esto es una emergencia…
                                </p>
                                <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                                    No me esperes a mí. Acude a un servicio de
                                    urgencia o llama a una línea de ayuda. Este
                                    formulario no reemplaza profesionales.
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </>
    );
}
